{
  "title": "How to Build and Publish Forms (Registration / Submission)",
  "description": "Create a registration or submission form, add fields, and publish it as the live version participants fill in.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, the form has a published version that participants can fill in for registration or submission.",
  "roleNote": "Competition Admin",
  "prerequisites": [
    "You have admin access to this competition",
    "You've planned out what fields you need to collect"
  ],
  "pathLabel": "Organizer Console → Competition Settings → Forms",
  "steps": [
    {
      "title": "1. Create the form and name it",
      "description": "Choose the form type (Registration Form / Team Form / Submission Form / Judge Conflict-of-Interest Form) and enter a name; you must type a name yourself — the system does not auto-fill generic placeholder text."
    },
    {
      "title": "2. Add fields",
      "description": "Add fields as needed; available types include short text, long text, number, email, date, single choice, multiple choice, dropdown, file upload, and more. Each field can be marked required, given a default value, given validation rules, and set to show or hide conditionally based on another field's answer. You can also give a field an optional section label; fields sharing the same label render grouped under one shared heading on the participant's side. A required checkbox (for example a Team declaration) means \"must be ticked\": without a tick the participant cannot complete registration and the server rejects it."
    },
    {
      "title": "3. Reorder fields (optional)",
      "description": "Reorder fields by drag, keyboard, or the move-up/down buttons; the new order shows immediately. If saving fails, the order automatically rolls back to what it was before and an error message asks you to try again."
    },
    {
      "title": "4. Preview the form",
      "description": "Once fields are added, preview the form as it currently stands to confirm field order, required markers, and conditional-visibility logic all behave as expected. The preview is interactive: you can pick dropdown options and tick checkboxes and immediately see how conditionally shown fields appear under each choice (for example which fields appear for Group A and which for Group B), so you can check every branch before publishing; it never submits or saves any data, and file fields cannot be uploaded in the preview."
    },
    {
      "title": "5. Publish the form",
      "description": "Once everything looks right, click \"Publish\" to make the current draft the live version; publishing is only meaningful — and only allowed — when there's an unpublished draft with changes."
    }
  ],
  "afterward": [
    "The form's status shows Published, and participants can fill it in against this version",
    "A published version's content can no longer be edited; the next time you make a change, the system automatically clones it into a new draft, without touching the currently live version or any answers already submitted",
    "A submitted answer always stays tied to whichever form version was live when it was submitted, regardless of any later version that gets published",
    "After a form is re-versioned, students who already completed registration are not sent back; but if their saved answers no longer meet the new version's required rules (for example a required field was added) they are marked \"Needs update\", and organizers can see the list on the Registration Data page"
  ],
  "nextSteps": "Once the registration and submission forms are built, see \"How to Create Rounds and Configure Their Schedule\" to bind the forms to the relevant round.",
  "faq": [
    {
      "q": "Can I edit fields on an already-published form directly?",
      "a": "No. A published version's content is immutable. The moment you start editing (adding/changing/removing a field), the system automatically clones the published version into a new draft, and your changes apply to that draft — nothing takes effect until you publish it. You don't need to manually create the new version yourself."
    },
    {
      "q": "Is the registration form built the same way as the submission form?",
      "a": "Yes. All four form types (registration, team, submission, judge conflict-of-interest) share the same creation and versioning mechanism — the only difference is what fields you add and what each one is used for."
    },
    {
      "q": "Why did I get an error when I clicked \"Publish\"?",
      "a": "The most common reason is that there's currently no unpublished draft with changes to publish — publishing is only meaningful when a draft version exists."
    },
    {
      "q": "After reordering fields I saw an error saying the order was rolled back — what does that mean?",
      "a": "It means this particular reorder attempt didn't save to the server; the screen automatically reverted to the order you had before the attempt, so there's no risk of an inconsistent order. Just try the drag or move-up/down action again. If you're editing a published version, the system first auto-creates a new draft version and then applies the reorder to it, the same auto-draft rule that applies when adding or removing a field."
    }
  ]
}
